/**
 * Product Delivery Checker
 * Custom replacement for Easy Zip Code Check app
 * Integrates with existing location filtering system
 */
class ProductDeliveryChecker {
  constructor() {
    this.widget = document.querySelector('.product-delivery-check');
    if (!this.widget) return;

    this.productId = this.widget.dataset.productId;
    this.availableZips = this.widget.dataset.availableZips || '';

    // Parse zone data: format is "title~~description~~color~~zipcodes||title~~description~~color~~zipcodes"
    this.zones = [];
    const zonesData = this.widget.dataset.zones || '';
    if (zonesData) {
      const zoneStrings = zonesData.split('||');
      zoneStrings.forEach(zoneStr => {
        const parts = zoneStr.split('~~');
        if (parts.length === 4) {
          this.zones.push({
            title: parts[0],
            description: parts[1],
            color: parts[2],
            zipCodes: parts[3]
          });
        }
      });
    }

    // console.log('[DeliveryChecker] Zone data loaded:', {
    //   zones: this.zones,
    //   zoneCount: this.zones.length,
    //   rawData: this.widget.dataset.zones
    // });

    this.input = document.getElementById('delivery-zip-input');
    this.button = document.getElementById('delivery-check-btn');
    this.resultDiv = document.getElementById('delivery-result');
    this.loadingDiv = document.getElementById('delivery-loading');
    this.currentLocationDiv = document.getElementById('current-location');

    this.init();
  }
  
  repositionWidget() {
    // Try to move widget after quantity selector
    const quantitySelector = document.querySelector('.quantity-selector, #quantity-selector, .product__quantity');

    if (quantitySelector && this.widget) {
      // Insert widget right after the quantity selector
      quantitySelector.parentNode.insertBefore(this.widget, quantitySelector.nextSibling);
      // console.log('[DeliveryChecker] Widget moved after quantity selector');
    }
  }

  init() {
    // console.log('[DeliveryChecker] Initializing for product', this.productId);

    // Move widget after quantity selector if it exists
    this.repositionWidget();

    // Load stored location
    this.loadStoredLocation();

    // Set up event listeners
    this.button?.addEventListener('click', () => this.checkDelivery());
    
    this.input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.checkDelivery();
      }
    });
    
    // Auto-check if location is already set
    if (typeof LocationUtils !== 'undefined') {
      const storedZip = LocationUtils.getZipCode();
      if (storedZip) {
        this.input.value = storedZip;
        this.checkDelivery(false); // Don't save again
      }
    }

    // Listen for location updates
    document.addEventListener('location:changed', (e) => {
      const zipCode = e.detail?.zipCode;
      // Only process if it's a valid 5-digit ZIP code (ignore placeholder values like 'Your location')
      if (zipCode && /^\d{5}$/.test(zipCode)) {
        this.input.value = zipCode;
        this.checkDelivery(false);
      }
    });
    
    // Change location link
    const changeLink = this.widget.querySelector('.change-location-link');
    changeLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.currentLocationDiv.style.display = 'none';
      this.input.value = '';
      this.input.focus();
      this.resultDiv.style.display = 'none';
    });
  }
  
  loadStoredLocation() {
    if (typeof LocationUtils === 'undefined') return;
    const storedZip = LocationUtils.getZipCode();
    if (storedZip) {
      const currentZipSpan = this.widget.querySelector('.current-zip');
      if (currentZipSpan) {
        currentZipSpan.textContent = storedZip;
        this.currentLocationDiv.style.display = 'block';
      }
    }
  }
  
  checkDelivery(saveLocation = true) {
    const zipCode = this.input.value.trim();
    
    // Validate zip code format
    if (!zipCode.match(/^\d{5}$/)) {
      this.showError('Please enter a valid 5-digit ZIP code');
      return;
    }
    
    // Show loading state
    this.showLoading();
    
    // Simulate processing delay for better UX
    setTimeout(() => {
      // Check if product has location data
      if (!this.availableZips || this.availableZips.trim() === '') {
        // No location data - show general availability
        this.showNoData();
      } else {
        // Check if zip is in available list
        const isAvailable = this.checkAvailability(this.availableZips, zipCode);
        
        if (isAvailable) {
          this.showAvailable(zipCode);
        } else {
          this.showUnavailable(zipCode);
        }
      }
      
      // Save location if requested (LocationUtils dispatches location:changed event)
      if (saveLocation) {
        LocationUtils.setLocation(zipCode, 'zip');
      }
      
      // Update current location display
      const currentZipSpan = this.widget.querySelector('.current-zip');
      if (currentZipSpan) {
        currentZipSpan.textContent = zipCode;
        this.currentLocationDiv.style.display = 'block';
      }
      
    }, 300);
  }
  
  checkAvailability(zipString, userZip) {
    return LocationUtils.isZipInRange(zipString, userZip);
  }
  
  showLoading() {
    this.hideAllResults();
    this.loadingDiv.style.display = 'inline-flex';
  }
  
  showAvailable(zipCode) {
    this.hideAllResults();
    this.resultDiv.style.display = 'block';
    const availableDiv = this.resultDiv.querySelector('.delivery-available');
    availableDiv.style.display = 'flex';
    availableDiv.querySelector('.delivery-zip').textContent = zipCode;

    // console.log('[DeliveryChecker] Showing available for ZIP:', zipCode);
    // console.log('[DeliveryChecker] Checking', this.zones.length, 'zones');

    // Find which zone contains this ZIP code
    const matchingZone = this.findZoneForZip(zipCode);

    if (matchingZone) {
      // console.log('[DeliveryChecker] Found matching zone:', matchingZone.title);

      const zonesContainer = availableDiv.querySelector('.delivery-zones-container');
      if (zonesContainer) {
        zonesContainer.style.display = 'block';
        zonesContainer.innerHTML = ''; // Clear existing content

        // Create zone display for ONLY the matching zone
        const zoneDiv = document.createElement('div');
        zoneDiv.className = 'delivery-zone-info';

        // Create zone badge
        const badgeDiv = document.createElement('div');
        badgeDiv.className = 'zone-badge';
        if (matchingZone.color) {
          badgeDiv.style.backgroundColor = matchingZone.color;
        }

        const titleElement = document.createElement('strong');
        titleElement.className = 'zone-title';
        titleElement.textContent = matchingZone.title;
        badgeDiv.appendChild(titleElement);

        // Create zone description
        const descElement = document.createElement('p');
        descElement.className = 'zone-description';
        if (matchingZone.description) {
          descElement.textContent = matchingZone.description;
        }

        // Add to zone div
        zoneDiv.appendChild(badgeDiv);
        if (matchingZone.description) {
          zoneDiv.appendChild(descElement);
        }

        zonesContainer.appendChild(zoneDiv);
      }
    } else {
      // console.log('[DeliveryChecker] No specific zone found for ZIP:', zipCode);
    }

    // Enable buy buttons if they were disabled
    this.updateBuyButtons(true);
  }

  findZoneForZip(zipCode) {
    // Loop through zones and check which one contains this ZIP
    for (const zone of this.zones) {
      const zipsInZone = LocationUtils.parseZipRanges(zone.zipCodes);
      if (zipsInZone.includes(zipCode)) {
        return zone;
      }
    }
    return null;
  }
  
  showUnavailable(zipCode) {
    this.hideAllResults();
    this.resultDiv.style.display = 'block';
    const unavailableDiv = this.resultDiv.querySelector('.delivery-unavailable');
    unavailableDiv.style.display = 'block';
    unavailableDiv.querySelector('.delivery-zip').textContent = zipCode;

    // Disable buy buttons when delivery is unavailable
    this.updateBuyButtons(false);
  }
  
  showNoData() {
    this.hideAllResults();
    this.resultDiv.style.display = 'block';
    const noDataDiv = this.resultDiv.querySelector('.delivery-no-data');
    noDataDiv.style.display = 'block';
    
    // Enable buy buttons for general availability
    this.updateBuyButtons(true);
  }
  
  showError(message) {
    this.hideAllResults();
    // Could add a dedicated error div or use alert
    alert(message);
  }
  
  hideAllResults() {
    this.loadingDiv.style.display = 'none';
    this.resultDiv.style.display = 'none';
    this.resultDiv.querySelectorAll('.delivery-available, .delivery-unavailable, .delivery-no-data, .delivery-zones-container').forEach(div => {
      div.style.display = 'none';
    });
  }
  
  updateBuyButtons(enable) {
    // Find buy buttons on the page
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    const buyNowBtn = document.querySelector('.shopify-payment-button');
    
    if (addToCartBtn) {
      addToCartBtn.disabled = !enable;
      if (!enable) {
        addToCartBtn.setAttribute('data-delivery-restricted', 'true');
        addToCartBtn.title = 'Please enter a valid delivery ZIP code';
      } else {
        addToCartBtn.removeAttribute('data-delivery-restricted');
        addToCartBtn.title = '';
      }
    }
    
    if (buyNowBtn) {
      buyNowBtn.style.pointerEvents = enable ? '' : 'none';
      buyNowBtn.style.opacity = enable ? '' : '0.5';
    }
  }
}

// Initialize on DOM ready - wait for LocationUtils to be available
function initDeliveryChecker() {
  if (typeof LocationUtils === 'undefined') {
    // LocationUtils not yet loaded, retry in 100ms
    setTimeout(initDeliveryChecker, 100);
    return;
  }
  window.productDeliveryChecker = new ProductDeliveryChecker();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDeliveryChecker);
} else {
  initDeliveryChecker();
}

// Hide delivery check for "Pick Up" variants, show for "Delivery" variants
function handleDeliveryCheckVisibility(variant) {
  const widget = document.querySelector('.product-delivery-check');
  if (!widget) return;

  const variantTitle = variant?.title || variant?.name || '';
  const isPickup = variantTitle.toLowerCase().includes('pick up') || variantTitle.toLowerCase().includes('pickup');

  widget.style.display = isPickup ? 'none' : 'block';
  // console.log('[DeliveryChecker] Variant changed:', variantTitle, 'Pickup:', isPickup);
}

// Listen for variant change events
document.addEventListener('variant:changed', (e) => {
  if (e.detail?.variant) handleDeliveryCheckVisibility(e.detail.variant);
});

document.addEventListener('variantchange', (e) => {
  if (e.detail) handleDeliveryCheckVisibility(e.detail);
});
// Edge Right: hide the compost guarantee banner
document.addEventListener('DOMContentLoaded', () => { if (document.querySelector('.product-delivery-check[data-edge-right="true"]')) { document.querySelectorAll('img[src*="Screenshot_2025-03-03_at_9.47.15_AM"], img[src*="/2714/7594/files/Screenshot_2025-04"]').forEach(i => { i.style.display = 'none'; }); } });

document.addEventListener('product:variant-change', (e) => {
  if (e.detail?.variant) handleDeliveryCheckVisibility(e.detail.variant);
});

/**
 * Simple Delivery Modal
 * Opens the delivery checker in a popup modal when clicking delivery link in variant buttons
 */
(function() {
  let modal = null;

  // Update delivery status text in variant buttons
  function updateDeliveryStatusText(zipCode) {
    const statusElements = document.querySelectorAll('.delivery-status-text');
    const widget = document.querySelector('.product-delivery-check');
    const availableZips = widget?.dataset?.availableZips || '';
    const isEdgeRight = widget?.dataset?.edgeRight === 'true';

    statusElements.forEach(el => {
      if (isEdgeRight) { el.textContent = 'Ships to your door in about 3-5 days'; return; }
      if (zipCode && typeof LocationUtils !== 'undefined') {
        const isAvailable = LocationUtils.isZipInRange(availableZips, zipCode);
        if (isAvailable) {
          el.innerHTML = `Available for delivery to <strong>${zipCode}</strong>`;
        } else {
          el.innerHTML = `No delivery to <strong>${zipCode}</strong>`;
        }
      } else {
        el.textContent = 'Check delivery availability';
      }
    });
  }

  // Create simple modal HTML (matching screenshot design)
  function createModal() {
    if (modal) return modal;

    const overlay = document.createElement('div');
    overlay.className = 'delivery-modal-overlay';
    overlay.innerHTML = `
      <div class="delivery-modal">
        <button type="button" class="delivery-modal-close" aria-label="Close">&times;</button>
        <h2 class="delivery-modal-title">Enter your delivery zip code</h2>
        <p class="delivery-modal-subtitle">Your zip code helps us provide you with the most accurate inventory, pricing, and delivery information.</p>
        <div class="delivery-modal-input-row">
          <input type="text" class="delivery-modal-input" placeholder="Enter your delivery zip code" maxlength="5" inputmode="numeric" autocomplete="postal-code">
          <button type="button" class="delivery-modal-submit" aria-label="Submit ZIP code">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Get elements
    const input = overlay.querySelector('.delivery-modal-input');
    const submitBtn = overlay.querySelector('.delivery-modal-submit');

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });

    // Close button
    overlay.querySelector('.delivery-modal-close').addEventListener('click', closeModal);

    // Submit ZIP
    submitBtn.addEventListener('click', () => submitZip(input.value));
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') submitZip(input.value);
    });

    // Only allow numeric input
    input.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
    });

    document.body.appendChild(overlay);
    modal = overlay;
    return modal;
  }

  // Submit ZIP code
  function submitZip(value) {
    const zipCode = value.trim();

    // Validate ZIP format
    if (typeof LocationUtils === 'undefined' || !LocationUtils.isValidZip(zipCode)) {
      const input = modal.querySelector('.delivery-modal-input');
      input.style.animation = 'none';
      input.offsetHeight;
      input.style.animation = 'shake 0.5s ease';
      input.focus();
      return;
    }

    // console.log('[DeliveryModal] Submitting ZIP:', zipCode);

    // Use the same function the header "Set Store" button uses
    // This handles everything: saving location, finding stores, updating UI
    const storePicker = document.querySelector('store-picker');
    if (storePicker) {
        storePicker.searchByZipCode(zipCode);
    }

    // Update variant button text
    updateDeliveryStatusText(zipCode);

    // Close modal
    closeModal();
  }

  // Open modal
  function openModal() {
    const m = createModal();
    m.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Pre-fill with stored ZIP
    if (typeof LocationUtils !== 'undefined') {
      const storedZip = LocationUtils.getZipCode();
      const input = m.querySelector('.delivery-modal-input');
      if (storedZip && input) {
        input.value = storedZip;
      }
    }

    // Focus input
    const input = m.querySelector('.delivery-modal-input');
    setTimeout(() => input?.focus(), 100);
  }

  // Close modal
  function closeModal() {
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  // Expose openModal globally for onclick handler
  window.openDeliveryModal = openModal;

  // Auto-populate on page load, or auto-open modal if no ZIP
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof LocationUtils !== 'undefined') {
      const storedZip = LocationUtils.getZipCode();
      if (storedZip) {
        updateDeliveryStatusText(storedZip);
      }
      // IP geolocation will auto-detect ZIP - no need to prompt
    }
  });

  // Listen for location changes
  document.addEventListener('location:changed', (e) => {
    if (e.detail?.zipCode) {
      updateDeliveryStatusText(e.detail.zipCode);
    }
  });

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && modal.style.display !== 'none') {
      closeModal();
    }
  });
})();

/**
 * Pickup Drawer Integration
 * Opens the store availability drawer and auto-populates from localStorage
 */
(function() {
  // Pick Up variants of a product with per-location stock (Storefront API, tokenless), once per page
  const pickupVariantsByHandle = {};
  function pickupVariants(handle) {
    if (!pickupVariantsByHandle[handle]) {
      const query = 'query($handle: String!) { product(handle: $handle) { variants(first: 100) { nodes { ' +
        'selectedOptions { name value } storeAvailability(first: 100) { nodes { available location { name } } } } } } }';
      pickupVariantsByHandle[handle] = fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { handle } })
      })
        .then(r => r.json())
        .then(d => (d.data?.product?.variants.nodes || []).filter(v => v.selectedOptions.some(o => /pick/i.test(o.value))))
        .catch(() => null);
    }
    return pickupVariantsByHandle[handle];
  }

  const sameStore = (a, b) => {
    const strip = name => (name || '').replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();
    return strip(a) === strip(b);
  };

  // true/false for the Pick Up variant matching the customer's other selections (e.g. size);
  // null when it can't tell (not inside a product section, lookup failed).
  async function pickupAvailableAt(el, storeName) {
    const section = el.closest('[data-product-handle]');
    if (!section) return null;
    const variants = await pickupVariants(section.dataset.productHandle);
    if (!variants || !variants.length) return null;
    const chosen = {};
    section.querySelectorAll('input[data-variant-input]:checked').forEach(input => { chosen[input.name] = input.value; });
    return variants
      .filter(v => v.selectedOptions.every(o => /pick/i.test(o.value) || !(o.name in chosen) || chosen[o.name] === o.value))
      .some(v => v.storeAvailability.nodes.some(s => s.available && sameStore(s.location.name, storeName)));
  }

  function setPickupStatus(el, prefix, storeName) {
    const strong = document.createElement('strong');
    strong.textContent = storeName;
    el.replaceChildren(prefix, strong);
  }

  // Update pickup status text in variant buttons, based on the selected store's actual stock
  let pickupStatusRun = 0;
  function updatePickupStatusText(storeName) {
    const run = ++pickupStatusRun;
    document.querySelectorAll('.pickup-status-text').forEach(async el => {
      if (!storeName) {
        el.textContent = 'Check pickup availability';
        return;
      }
      setPickupStatus(el, 'Checking pickup at ', storeName);
      const available = await pickupAvailableAt(el, storeName);
      if (run !== pickupStatusRun) return;
      setPickupStatus(el, available === false ? 'Pickup unavailable at ' : 'Pickup available at ', storeName);
    });
  }

  // Re-check when the customer picks a different size/option
  document.addEventListener('change', (e) => {
    if (!e.target.matches?.('input[data-variant-input]') || typeof LocationUtils === 'undefined') return;
    const store = LocationUtils.getStore();
    if (store?.name) updatePickupStatusText(store.name);
  });

  // Open pickup drawer
  function openPickupDrawer() {
    // Try multiple selectors for the drawer opener
    const drawerOpener =
      document.querySelector('.store-availability a.js-drawer-open-availability') ||
      document.querySelector('[aria-controls="StoreAvailabilityDrawer"]') ||
      document.querySelector('[data-store-availability-modal-opener]');

    if (drawerOpener) {
      drawerOpener.click();
      return;
    }

    // Fallback: try to open drawer directly
    const drawer = document.getElementById('StoreAvailabilityDrawer');
    if (drawer) {
      // Trigger theme's drawer open logic
      document.dispatchEvent(new CustomEvent('drawer:open', {
        detail: { id: 'StoreAvailabilityDrawer' }
      }));
    }
  }

  // Expose globally
  window.openPickupDrawer = openPickupDrawer;

  // Auto-populate on page load
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof LocationUtils !== 'undefined') {
      const store = LocationUtils.getStore();
      if (store?.name) {
        updatePickupStatusText(store.name);
      }
    }
  });

  // Listen for store selection updates
  document.addEventListener('store:selected', (e) => {
    if (e.detail?.name) {
      updatePickupStatusText(e.detail.name);
    }
  });

  // Listen for location changes from header store picker
  document.addEventListener('location:changed', (e) => {
    // Wait for store-picker to auto-select nearest store
    setTimeout(() => {
      if (typeof LocationUtils !== 'undefined') {
        const store = LocationUtils.getStore();
        updatePickupStatusText(store?.name);
      }
    }, 500);
  });
})();